import { CheckCircle2, ChevronRight, ClipboardList, Clock, Copy, ExternalLink, History, Rocket, Square, Trash2, UserPlus, Users, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/client';
import type { Collaborator, SurveyDetail, SurveyVersion } from '../../api/types';
import { formatDate } from '../../format';
import { useI18n } from '../../i18n';
import { useStoredState } from '../../hooks/useStoredState';
import { ColResizer } from '../Pane';
import { CollectionCard } from './CollectionCard';
import { ShareLinkCard } from './ShareLinkCard';
import { CollapsibleCard } from '../CollapsibleCard';
import { Field, StatusPill, useToast } from '../ui';
import { useAuth } from '../../auth';
import { roleLabel } from '../../roles';

interface Props {
  survey: SurveyDetail;
  dirty: boolean;
  onPublish: () => void;
  onClose: () => void;
  busy: boolean;
  onSurveyUpdate(survey: SurveyDetail): void;
}

export function SharePanel({ survey, dirty, onPublish, onClose, busy, onSurveyUpdate }: Props) {
  const toast = useToast();
  const { t } = useI18n();
  const [sideW, setSideW] = useStoredState('survey.share.side_w', 0);
  const can = (p: string) => survey.permissions.includes(p as never);
  // Approving a questionnaire version is a role permission, not one tied to this questionnaire.
  const canApproveSurvey = useAuth().can('survey.approve');
  const url = `${window.location.origin}/s/${survey.slug || survey.code}`;
  const [versions, setVersions] = useState<SurveyVersion[]>([]);

  useEffect(() => {
    api.versions(survey.id).then((r) => setVersions(r.versions), () => {});
  }, [survey.id, survey.publishedVersion]);

  const live = survey.status === 'published';
  const latest = versions.reduce<SurveyVersion | undefined>((a, v) => (!a || v.version > a.version ? v : a), undefined);
  const blocking = survey.issues.length > 0;
  async function decideSurvey(status: 'approved'|'rejected') {
    try { onSurveyUpdate(await api.approveSurvey(survey.id,status)); toast(t(status==='approved'?'approval.survey_approved':'approval.survey_rejected')); }
    catch(e) { toast(e instanceof ApiError?e.message:t('common.action_failed'),'error'); }
  }

  const fieldWork = can('survey.enumerators.assign');

  return (
    <div className="share-page">
      <div className="share-grid" style={sideW ? ({ '--side-w': `${sideW}px` } as React.CSSProperties) : undefined}>
        <div className="share-col" data-col="side">
          <div className="card">
            <div className="card-head">
              <div className="tile">
                <Rocket />
              </div>
              <div className="grow">
                <h2>{t('share.publishing')}</h2>
                <div className="sub">
                  {survey.publishedVersion
                    ? t('share.published_on', { version: survey.publishedVersion, date: formatDate(survey.publishedAt, true) })
                    : t('share.not_published')}
                </div>
              </div>
              <StatusPill status={survey.status} />
            </div>
            <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
              <div className="link-box">
                <span title={survey.requireAuth ? t('share.requires_signin') : t('share.anyone')}>{url}</span>
                <button
                  className="btn sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(url).then(
                      () => toast(t('share.copied')),
                      () => toast(t('share.copy_failed'), 'error'),
                    );
                  }}
                >
                  <Copy /> {t('common.copy')}
                </button>
                <a className="btn sm" href={url} target="_blank" rel="noreferrer">
                  <ExternalLink /> {t('common.open')}
                </a>
              </div>
              <p className="hint" style={{ margin: '-4px 0 0' }}>
                {survey.requireAuth ? t('share.requires_signin') : t('share.anyone')} · {t('share.code_short', { code: survey.code })}
              </p>
              {blocking && (
                <div className="banner warn">
                  <div>
                    <strong>{t('share.fix_before')}</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {survey.issues.slice(0, 6).map((i, n) => (
                        <li key={n}>{i.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {live && survey.hasUnpublishedChanges && !blocking && (
                <div className="banner">{t('share.live_has_changes', { version: survey.publishedVersion })}</div>
              )}
              {survey.status === 'closed' && <div className="banner warn">{t('share.closed_note')}</div>}
              {survey.status === 'archived' && <div className="banner warn">{t('share.archived_note')}</div>}
              <div className={`banner approval-banner ${survey.approvalStatus === 'approved' ? 'success' : 'warn'}`}>
                {survey.approvalStatus === 'approved' ? <CheckCircle2 /> : survey.approvalStatus === 'rejected' ? <XCircle /> : <Clock />}
                <div className="grow">
                  <div>{t(`approval.survey_${survey.approvalStatus}`)}</div>
                  {survey.approvalNote && <div className="approval-note">“{survey.approvalNote}”</div>}
                </div>
                {canApproveSurvey && (
                  <div className="btn-row approval-actions">
                    <button className="btn sm" disabled={busy || dirty || survey.approvalStatus === 'approved'} onClick={() => void decideSurvey('approved')}>{t('approval.approve')}</button>
                    <button className="btn sm" disabled={busy || dirty || survey.approvalStatus === 'rejected'} onClick={() => void decideSurvey('rejected')}>{t('approval.reject')}</button>
                  </div>
                )}
              </div>
              {can('survey.publish') && survey.status !== 'archived' && (
                <div className="btn-row">
                  <button className="btn accent" onClick={onPublish} disabled={busy || dirty || blocking || survey.approvalStatus !== 'approved' || (live && !survey.hasUnpublishedChanges)}>
                    <Rocket />
                    {dirty
                      ? t('share.saving')
                      : !survey.publishedVersion
                        ? t('share.publish')
                        : live
                          ? survey.hasUnpublishedChanges
                            ? t('share.publish_changes')
                            : t('share.up_to_date')
                          : t('share.reopen_publish')}
                  </button>
                  {live && (
                    <button className="btn" onClick={onClose} disabled={busy}>
                      <Square /> {t('share.close_responses')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <ShareLinkCard survey={survey} canEdit={can('survey.share')} onSurveyUpdate={onSurveyUpdate} />

          <CollectionCard
            surveyId={survey.id}
            canEdit={can('survey.publish') && survey.status !== 'archived'}
            hasQuotas={(survey.definition.quotas?.length ?? 0) > 0}
            onSaved={() => void api.getSurvey(survey.id).then(onSurveyUpdate, () => {})}
          />
        </div>

        <ColResizer label={t('builder.resize_panel')} width={sideW} onWidth={setSideW} onReset={() => setSideW(0)} />

        <div className="share-col">
          {fieldWork && (
            <Link to={`/surveys/${survey.id}/assignments`} className="card link-card">
              <div className="tile">
                <ClipboardList />
              </div>
              <div className="grow">
                <h2>{t('share.field_work')}</h2>
                <div className="sub">{t('share.field_work_moved')}</div>
              </div>
              <ChevronRight />
            </Link>
          )}
          <Collaborators survey={survey} />
          {versions.length > 0 && (
            <CollapsibleCard
              storageKey="survey.share.open.versions"
              defaultOpen={false}
              icon={<History />}
              title={t('share.versions')}
              sub={t('share.versions_sub', {
                count: versions.length,
                version: latest!.version,
                date: formatDate(latest!.publishedAt, true),
              })}
            >
              <div className="version-list">
                {versions.map((v) => (
                  <div className="version-row" key={v.version}>
                    <span className="pill">v{v.version}</span>
                    <span className="grow ellipsis">{v.title}</span>
                    <span className="small muted">{formatDate(v.publishedAt, true)}</span>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          )}
        </div>
      </div>
    </div>
  );
}

function Collaborators({ survey }: { survey: SurveyDetail }) {
  const toast = useToast();
  const { t } = useI18n();
  const canShare = survey.permissions.includes('survey.share');
  const [list, setList] = useState<Collaborator[] | null>(null);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<'editor' | 'viewer'>('viewer');
  const [fields, setFields] = useState<Record<string, string>>({});

  const load = useCallback(() => api.collaborators(survey.id).then((r) => setList(r.collaborators), () => setList([])), [survey.id]);
  useEffect(() => {
    void load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setFields({});
    try {
      const r = await api.addCollaborator(survey.id, email, permission);
      setList(r.collaborators);
      setEmail('');
      toast(t('share.granted'));
    } catch (err) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fields).length) setFields(err.fields);
        else toast(err.message, 'error');
      }
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div className="tile">
          <Users />
        </div>
        <div className="grow">
          <h2>{t('share.team')}</h2>
          <div className="sub">{t('share.team_sub')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-16" style={{ paddingTop: 0 }}>
        <div className="row gap-12">
          <span className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
            {(survey.owner.name || survey.owner.email).slice(0, 1).toUpperCase()}
          </span>
          <div className="grow">
            <div>{survey.owner.name || survey.owner.email}</div>
            <div className="small muted">{survey.owner.email}</div>
          </div>
          <span className="pill green">{t('share.owner')}</span>
        </div>
        {list?.map((c) => (
          <div key={c.userId} className="row gap-12">
            <span className="avatar" style={{ width: 30, height: 30, fontSize: 12, background: 'var(--tile)' }}>
              {(c.name || c.email).slice(0, 1).toUpperCase()}
            </span>
            <div className="grow">
              <div>{c.name || c.email}</div>
              <div className="small muted">
                {c.email} · {roleLabel(t, c.role)}
              </div>
            </div>
            <span className={`pill ${c.permission === 'editor' ? 'blue' : ''}`}>{t(`share.${c.permission}`)}</span>
            {canShare && (
              <button
                className="btn ghost sm icon"
                aria-label={t('share.remove', { email: c.email })}
                onClick={async () => {
                  try {
                    await api.removeCollaborator(survey.id, c.userId);
                    await load();
                  } catch (e) {
                    toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
                  }
                }}
              >
                <Trash2 />
              </button>
            )}
          </div>
        ))}
        {canShare && (
          <form onSubmit={add} className="stack gap-8">
            <div className="invite-grid">
              <Field label={t('share.invite')} error={fields.email || fields.permission}>
                <input className="input" type="email" required value={email} placeholder={t('share.invite_placeholder')} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <select className="select" value={permission} aria-label={t('share.permission')} onChange={(e) => setPermission(e.target.value as 'editor' | 'viewer')}>
                <option value="viewer">{t('share.viewer')}</option>
                <option value="editor">{t('share.editor')}</option>
              </select>
              <button className="btn primary">
                <UserPlus /> {t('common.add')}
              </button>
            </div>
            <span className="hint">{t('share.invite_hint')}</span>
          </form>
        )}
      </div>
    </div>
  );
}
